<template>
  <div>
    <modal
      ref="videoModal"
      class="video-component"
      :class="[
        isNotChatTab ? 'is-not-chat-tab' : 'is-chat-tab',
        isFullScreen ? 'is-fullscreen' : '',
      ]"
      id="video-component"
      name="video-component"
      :width="modalWidth"
      @before-close="$emit('beforeClose', $event)"
      @before-open="handleBeforeOpen"
    >
      <div class="video-body">
        <div class="video-header" ref="dragHeader">
          <div>
            <span>ビデオ通話</span>
            <span v-if="currentChannelUser">{{ `${currentChannelUser.family_name || ''} ${currentChannelUser.given_name ||
              ''}`.trim()
              || '-' }}
            </span>
          </div>
          <div>
            <span v-if="networkDisplayStatus" class="video-component" :class="{
              'network-high': rtc.networkStatus === networkStatus.HIGH.name,
              'network-medium': rtc.networkStatus === networkStatus.MEDIUM.name,
              'network-low': rtc.networkStatus === networkStatus.LOW.name
                || rtc.connectionStatus === 'reconnecting'
                || rtc.connectionStatus === 'disconnected',
            }">
              ネットワークの状態：{{ networkDisplayStatus }}
            </span>
          </div>
          <div v-if="isUserJoined || isUserLeaved">
            <transition name="slide-right-left">
              <div class="video-notification">
                {{ isUserJoined && !isUserLeaved ? '患者は診察ルームに参加しています。' : '患者は診察ルームを退出しました。' }}
              </div>
            </transition>
          </div>
        </div>

        <div class="video-controls">
          <button class="full-screen" @click="toggleFullScreen">
            <component :is="isFullScreen ? 'ShrinkIcon' : 'ExpandIcon'" />
          </button>
        </div>

        <img v-if="userMicOff" :src="micUserIcon" class="mic-icon-user" alt="Micro" />
        <div class="video-container">
          <div class="video-content" id="video-content"></div>
          <div class="video-content-user" id="video-content-user"></div>
        </div>
        <div class="video-footer">
          <div class="video-item" v-if="isCall">
            <button class="video-btn video-btn-plan" @click="showPlanListModal()">
              プラン一覧
            </button>
          </div>
          <div class="video-item">
            <img :src="camIcon" @click="camToggle()" alt="Camera" />
          </div>
          <div class="video-item">
            <button v-if="isCall" class="video-btn video-btn-end" @click="leaveCalling">
              退出する
            </button>
            <button v-else class="video-btn video-btn-call" id="join" :disabled="!isCanJoin" @click="joinCalling">
              開始する
            </button>
          </div>
          <div class="video-item">
            <img :src="micIcon" @click="micToggle()" alt="Micro" />
          </div>
        </div>

      </div>
    </modal>

    <PlanListPopup ref="plan-list-popup" :name="'plan-list-popup'" :is-show="isShowPlanListModal" />
  </div>
</template>

<script>
import { Action } from 'vuex-class';
import { Component, Prop, Mixins } from 'vue-property-decorator';
import AgoraRTC from 'agora-rtc-sdk-ng';
import Toast from '@common/helpers/toast';
import ApiHelper from 'api-helper';
import CamOn from '@/assets/img/camera.svg';
import CamOff from '@/assets/img/camera-lock.svg';
import MicOn from '@/assets/img/mic.svg';
import MicOff from '@/assets/img/mic-lock.svg';
import WindowSizeMixin from '@common/mixins/WindowSizeMixin';

import PlanListPopup from '@components/v2/patient/message/PlanListPopup.vue';
import ShrinkIcon from './ShrinkIcon.vue';
import ExpandIcon from './ExpandIcon.vue';

@Component({
  components: {
    PlanListPopup,
    ShrinkIcon,
    ExpandIcon,
  },
})
export default class VideoComponent extends Mixins(WindowSizeMixin) {
  @Prop() currentChannelUser;
  @Prop() isNotChatTab;
  @Action('video_call/setVideoCall') setVideoCall;

  agoraTokenData = {};
  hasMic = true;
  hasCamera = true;
  isCall = false;
  hasUser = false;
  userMicOff = false;
  isPublishedVideo = false;
  isCanJoin = true;
  isUserJoined = false;
  isUserLeaved = false;
  isFullScreen = false;


  networkStatus = {
    HIGH: {
      name: '良い',
      quantity: [1, 2],
    },
    MEDIUM: {
      name: '中程度',
      quantity: [3, 4],
    },
    LOW: {
      name: '電波が非常に弱い',
      quantity: [5, 6],
    },
  };

  rtc = {
    localAudioTrack: null,
    localVideoTrack: null,
    client: null,
    networkQuantity: null,
    networkStatus: null,
    connectionStatus: null,
    originalVideoConfig: null,
    localPlayerContainer: null,
  };

  // Debounce counters for network-quality (not persisted in rtc to avoid reactivity overhead)
  _pendingQualityLevel = null;
  _pendingQualityCount = 0;
  _appliedLevel = null;
  _videoDisabledByLowQuality = false;
  DEBOUNCE_THRESHOLD = 2;

  // Right after an Agora reconnect, uplinkNetworkQuality is frequently
  // mis-estimated as LOW for the first few samples while the SDK re-measures
  // real bandwidth — even when the network has actually recovered. To avoid
  // reacting to that transient blip, we require a longer streak of consecutive
  // LOW samples (RECONNECT_GRACE_LOW_THRESHOLD) during a short window after
  // reconnect (RECONNECT_GRACE_MS) before disabling the local video again.
  // If the doctor's network is genuinely still weak, it will keep reporting
  // LOW past that longer streak and the disable will still kick in — just a
  // few seconds later than usual — so real sustained bad network is not masked.
  _reconnectGraceUntil = null;
  RECONNECT_GRACE_MS = 8000;
  RECONNECT_GRACE_LOW_THRESHOLD = 4;

  // Watchdog: Agora's uplinkNetworkQuality can keep reporting quality=0
  // (unknown) for a long stretch after a real network drop while the SDK
  // re-measures bandwidth — handleNetworkQuantity() skips quality=0 samples
  // entirely, so as long as that keeps happening the local video stays
  // hidden/disabled indefinitely even though the connection itself
  // (connection-state-change) already reports CONNECTED and is stable. This
  // timer forces the local video back on if it is still hidden
  // VIDEO_RESTORE_WATCHDOG_MS after being disabled, as long as:
  //   (a) the connection has not dropped again, AND
  //   (b) no valid (non-zero) quality sample has arrived in the last
  //       VIDEO_RESTORE_WATCHDOG_MS — i.e. the metric is genuinely stuck.
  // This distinguishes "stuck at quality=0" (force-restore is correct) from
  // "network is genuinely still weak" (Agora keeps emitting LOW samples every
  // ~2s, so _lastValidQualitySampleAt stays recent, and the watchdog must NOT
  // force-restore to avoid a 20s flicker loop).
  _videoRestoreWatchdogTimer = null;
  _lastValidQualitySampleAt = null;
  VIDEO_RESTORE_WATCHDOG_MS = 20000;

  options = {
    // Pass your App ID here.
    appId: 'Your App ID',
    // Set the channel name.
    channel: 'test',
    // Pass your temp token here.
    token: 'Your temp token',
    // Set the user ID.
    uid: 123456,
  };

  isShowPlanListModal = false;

  resetCallState() {
    this.isUserJoined = false;
    this.isUserLeaved = false;
    this.userMicOff = false;
    this.rtc.networkStatus = null;
    this.rtc.networkQuantity = null;
    this.rtc.connectionStatus = null;
    this.rtc.localPlayerContainer = null;
    this._pendingQualityLevel = null;
    this._pendingQualityCount = 0;
    this._appliedLevel = null;
    this._videoDisabledByLowQuality = false;
    this._reconnectGraceUntil = null;
    this._lastValidQualitySampleAt = null;
    this.clearVideoRestoreWatchdog();
    this.isCall = false;
    this.isCanJoin = true;
    this.hasMic = true;
    this.hasCamera = true;
    this.isPublishedVideo = false;
  }

  // (Re)plays the local video track into `container`. Agora's play() always
  // creates a NEW <video> element inside the container instead of reusing the
  // existing one, so calling it while the track is already playing — which
  // can happen here because three independent restore paths
  // (forceRestoreVideoIfStale, handleConnectionStateChange,
  // handleNetworkQuantity) can all try to restore the same track around the
  // same reconnect — would otherwise leave a stale blank <video> element next
  // to the live one. stop() first so every restore always starts from a
  // clean container, and share one implementation so the three call sites
  // can't drift out of sync.
  restartLocalVideoTrack(container) {
    if (this.rtc.localVideoTrack.isPlaying) {
      this.rtc.localVideoTrack.stop();
    }
    this.rtc.localVideoTrack.play(container);
  }

  clearVideoRestoreWatchdog() {
    if (this._videoRestoreWatchdogTimer) {
      clearTimeout(this._videoRestoreWatchdogTimer);
      this._videoRestoreWatchdogTimer = null;
    }
  }

  scheduleVideoRestoreWatchdog() {
    this.clearVideoRestoreWatchdog();
    this._videoRestoreWatchdogTimer = setTimeout(() => {
      this.forceRestoreVideoIfStale();
    }, this.VIDEO_RESTORE_WATCHDOG_MS);
  }

  // Force-restores the local video if the quality metric is genuinely stuck
  // (see VIDEO_RESTORE_WATCHDOG_MS above). Three conditions must ALL hold:
  //   1. Connection is stable (not reconnecting/disconnected).
  //   2. Video is currently disabled by the LOW branch.
  //   3. No valid (non-zero) quality sample has been received during the
  //      entire watchdog window — i.e. Agora has been reporting only
  //      quality=0 (unknown) the whole time, meaning the metric itself is
  //      stuck, not that the network is still genuinely weak.
  // If Agora is still emitting LOW samples every ~2s, condition 3 fails and
  // the watchdog does nothing — this prevents the 20s flicker loop that
  // would occur if we force-restored while a real LOW streak is ongoing.
  forceRestoreVideoIfStale() {
    this._videoRestoreWatchdogTimer = null;

    if (this.rtc.connectionStatus !== null) {
      // Still reconnecting/disconnected — not a stuck-metric situation.
      return;
    }
    if (!this._videoDisabledByLowQuality
      || !this.rtc.localVideoTrack
      || !this.rtc.localPlayerContainer) {
      return;
    }

    // Condition 3: a recent valid sample means the metric is not stuck —
    // Agora is still actively reporting quality (likely LOW), so the network
    // is genuinely weak and we must not force-restore. Re-arm the watchdog
    // so that if the metric later transitions to quality=0 (stuck) while
    // video is still hidden, the protection remains active. Without this
    // re-arm the timer dies here and the original bug (permanent blank
    // preview) can return.
    const now = Date.now();
    if (this._lastValidQualitySampleAt !== null
      && (now - this._lastValidQualitySampleAt) < this.VIDEO_RESTORE_WATCHDOG_MS) {
      this.scheduleVideoRestoreWatchdog();
      return;
    }

    try {
      this._videoDisabledByLowQuality = false;
      // Allow a fresh, genuinely-reported LOW sample to re-hide it later.
      this._appliedLevel = null;

      if (this.hasCamera) {
        this.rtc.localVideoTrack.setEnabled(true);
        this.restartLocalVideoTrack(this.rtc.localPlayerContainer);
      }

      const localVideoContentUser = document.getElementById('video-content-user');
      if (localVideoContentUser) {
        localVideoContentUser.style.display = 'flex';
      }
    } catch (error) {
      // Best-effort — a later network-quality sample or the next reconnect
      // will retry.
    }
  }

  get networkDisplayStatus() {
    if (this.rtc.connectionStatus === 'disconnected') {
      return '接続が切れました';
    }
    if (this.rtc.connectionStatus === 'reconnecting') {
      return '再接続中...';
    }
    return this.rtc.networkStatus;
  }

  async handleConnectionStateChange(curState) {
    if (curState === 'RECONNECTING') {
      this.rtc.connectionStatus = 'reconnecting';
      // A real interruption is happening again — the watchdog's premise (the
      // connection has been stable this whole time) no longer holds.
      this.clearVideoRestoreWatchdog();
    } else if (curState === 'DISCONNECTED') {
      this.rtc.connectionStatus = 'disconnected';
      this.clearVideoRestoreWatchdog();
    } else if (curState === 'CONNECTED') {
      const wasInterrupted = this.rtc.connectionStatus === 'reconnecting'
        || this.rtc.connectionStatus === 'disconnected';
      this.rtc.connectionStatus = null;
      // Force quality to re-display after reconnect: clear applied level so the
      // next network-quality event will update rtc.networkStatus even if the
      // quality value hasn't changed since before the disconnect.
      this._appliedLevel = null;
      // Start a short grace window during which a LOW reading needs a longer
      // streak to be trusted (see RECONNECT_GRACE_MS/RECONNECT_GRACE_LOW_THRESHOLD)
      // — filters out the transient mis-estimated LOW samples Agora tends to
      // report right after reconnecting, without permanently masking a network
      // that is genuinely still weak.
      this._reconnectGraceUntil = Date.now() + this.RECONNECT_GRACE_MS;

      // After Agora auto-reconnects, the local video track's render pipeline
      // can stall (shows as a frozen/colored block) even though the track
      // itself is still published and enabled. A real reconnect must win over
      // a stale low-quality disable: uplinkNetworkQuality can keep reporting 0
      // (unknown) for a while — or indefinitely in some environments — right
      // after reconnecting, and handleNetworkQuantity() skips quality=0
      // entirely. If the LOW branch had already disabled/stopped the local
      // video track before the drop, _videoDisabledByLowQuality would then
      // never clear and the camera would stay dark forever even though the
      // connection recovered. So restore local video unconditionally here and
      // let a fresh LOW reading (if the network is genuinely still bad)
      // re-disable it.
      if (wasInterrupted && this.rtc.localVideoTrack && this.rtc.localPlayerContainer) {
        this._videoDisabledByLowQuality = false;

        if (this.hasCamera) {
          try {
            this.rtc.localVideoTrack.setEnabled(true);
            this.restartLocalVideoTrack(this.rtc.localPlayerContainer);
            if (!this.isPublishedVideo) {
              await this.rtc.client.publish([this.rtc.localVideoTrack]);
              this.isPublishedVideo = true;
            }
          } catch (error) {
            // Restoring the local video track after reconnect can legitimately
            // fail (e.g. track already closed) — swallow it, a later HIGH/MEDIUM
            // network-quality sample or the next reconnect will retry.
          }
        }

        const localVideoContentUser = document.getElementById('video-content-user');
        if (localVideoContentUser) {
          localVideoContentUser.style.display = 'flex';
        }
      }
    }
  }

  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  get modalWidth() {
    if (this.isFullScreen) {
      return '100vw';
    }
    if (this.windowWidth <= 767) {
      return '100%';
    }
    return this.isNotChatTab ? 500 : 540;
  }

  handleBeforeOpen(event) {
    this.resetCallState();
    this.getVideoToken(event.params.currentChannel);
    setTimeout(() => {
      this.enableDragging();
    }, 50);
  }

  enableDragging() {
    const modal = document.querySelector('.v--modal-box');
    const header = this.$refs.dragHeader;

    if (!modal || !header) {
      return;
    }

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    header.style.cursor = 'move';

    const onMouseDown = (e) => {
      isDragging = true;

      // Get initial mouse and modal position
      startX = e.clientX;
      startY = e.clientY;

      const rect = modal.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;

      // Fix modal if there is no specific style
      modal.style.position = 'fixed';
      modal.style.margin = '0';

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!isDragging) {
        return;
      }

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      let newLeft = initialLeft + dx;
      let newTop = initialTop + dy;

      const modalRect = modal.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      const maxLeft = windowWidth - modalRect.width;
      const maxTop = windowHeight - modalRect.height;

      newLeft = Math.min(Math.max(0, newLeft), maxLeft);
      newTop = Math.min(Math.max(0, newTop), maxTop);

      modal.style.left = `${newLeft}px`;
      modal.style.top = `${newTop}px`;
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    header.addEventListener('mousedown', onMouseDown);
  }

  micToggle() {
    this.hasMic = !this.hasMic;
    this.rtc.localAudioTrack.setEnabled(this.hasMic);
  }

  async camToggle() {
    // Block camera toggle only while the connection itself is down.
    // Do NOT block on networkStatus === LOW: after reconnect the status may
    // still be stale (LOW) even though the connection is already CONNECTED.
    if (this.rtc.connectionStatus === 'disconnected'
      || this.rtc.connectionStatus === 'reconnecting') {
      return;
    }

    this.hasCamera = !this.hasCamera;
    this.rtc.localVideoTrack.setEnabled(this.hasCamera);
  }

  get camIcon() {
    return this.hasCamera ? CamOn : CamOff;
  }

  get micIcon() {
    return this.hasMic ? MicOn : MicOff;
  }

  get micUserIcon() {
    return this.userMicOff ? MicOff : MicOn;
  }

  async getVideoToken(currentChannel) {
    await ApiHelper.getApi('ChannelApi')
      .agoraToken(currentChannel.id, {
        with: 'users,latest_message,users.latestReservation',
      })
      .then((response) => {
        this.isCanJoin = false;
        this.agoraTokenData = response.data;
        this.options = {
          appId: response.data.app_id,
          channel: response.data.channel_uuid,
          token: response.data.token,
          uid: Number(response.data.uid),
        };
        this.startBasicCall();
        this.isCanJoin = true;
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(error.response.data.message);
        }
      })
      .finally(() => {
        console.log();
      });
  }

  async startBasicCall() {
    // Create an AgoraRTCClient object.
    this.rtc.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

    // Listen for real connection state changes (CONNECTED/RECONNECTING/DISCONNECTED)
    this.rtc.client.on('connection-state-change', (curState, revState, reason) => {
      this.handleConnectionStateChange(curState);
    });

    // Listen User joined room
    this.rtc.client.on('user-joined', (user) => {
      this.isUserJoined = true;
      this.isUserLeaved = false;
    });

    // Listen User left room
    this.rtc.client.on('user-left', (user) => {
      this.isUserLeaved = true;
      this.isUserJoined = false;
    });

    // Listen for the 'user-published' event, from which you can get an AgoraRTCRemoteUser object.
    this.rtc.client.on('user-published', async (user, mediaType) => {
      // Subscribe to the remote user when the SDK triggers the 'user-published' event
      await this.rtc.client.subscribe(user, mediaType, {
        audio: {
          codec: 'opus',
          noiseSuppression: true, // Bật khử tiếng ồn
          echoCancellation: true, // Bật echo cancellation
        },
        video: {
          fec: true,
        },
      });
      console.log('subscribe success');

      // If the remote user publishes a video track.
      if (mediaType === 'video') {
        // Get the RemoteVideoTrack object in the AgoraRTCRemoteUser object.
        const remoteVideoTrack = user.videoTrack;
        const videoContent = document.getElementById('video-content');
        // Reuse a stale container for the same uid in place (e.g. after a
        // network reconnect user-published fires again without an
        // intervening user-unpublished) — clearing its content, rather than
        // removing and recreating the element, avoids leaving a second blank
        // box alongside the freshly-created one.
        let remotePlayerContainer = document.getElementById(user.uid.toString());
        if (remotePlayerContainer) {
          // remoteVideoTrack is the SDK's per-uid RemoteVideoTrack instance,
          // reused across a reconnect — stop it before detaching its old
          // <video> element so it cleanly restarts into the container instead
          // of continuing to render into a node no longer in the document.
          if (remoteVideoTrack?.isPlaying) {
            remoteVideoTrack.stop();
          }
          remotePlayerContainer.innerHTML = '';
        } else {
          // Dynamically create a container in the form of a DIV element for playing the remote video track.
          remotePlayerContainer = document.createElement('div');
          // Specify the ID of the DIV container. You can use the uid of the remote user.
          remotePlayerContainer.id = user.uid.toString();
          // remotePlayerContainer.textContent = 'Remote user ' + user.uid.toString();
          remotePlayerContainer.style.width = '100%';
          remotePlayerContainer.style.height = 'calc(100vh - 12vh)';
          videoContent.append(remotePlayerContainer);
        }
        // document.body.append(remotePlayerContainer);

        // Play the remote video track.
        // Pass the DIV container and the SDK dynamically creates a player in the container for playing the remote video track.
        // remoteVideoTrack.play(remotePlayerContainer);
        remoteVideoTrack.play(remotePlayerContainer, { fit: 'contain', mirror: false });
      }

      // If the remote user publishes an audio track.
      if (mediaType === 'audio') {
        this.userMicOff = false;
        // Get the RemoteAudioTrack object in the AgoraRTCRemoteUser object.
        const remoteAudioTrack = user.audioTrack;
        // Play the remote audio track. No need to pass any DOM element.
        remoteAudioTrack.play();
      }

      // Listen for the 'user-unpublished' event
      this.rtc.client.on('user-unpublished', (user, mediaType) => {
        // Get the dynamically created DIV container.
        const remotePlayerContainer = document.getElementById(user.uid);
        // Destroy the container.

        // If the remote user publishes an audio track.
        if (mediaType === 'audio') {
          this.userMicOff = true;
        }
        if (mediaType === 'video') {
          remotePlayerContainer.remove();
        }
      });
    });
  }

  async joinCalling() {
    this.isCanJoin = false;

    // document.getElementById('join').onclick = async function () {
    // Join an RTC channel.
    await this.rtc.client.join(
      this.options.appId,
      this.options.channel,
      this.options.token,
      this.options.uid,
    );
    // Create a local audio track from the audio sampled by a microphone.
    this.rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      encoderConfig: {
        bitrate: 32,
        sampleRate: 32000,
        codec: 'opus',
      },
      AEC: true,
      AGC: true,
      ANS: true,
    });
    // Create a local video track from the video captured by a camera.
    this.rtc.localVideoTrack = await AgoraRTC.createCameraVideoTrack();
    // Publish the local audio and video tracks to the RTC channel.
    await this.rtc.client.publish([
      this.rtc.localAudioTrack,
      this.rtc.localVideoTrack,
    ]);
    this.isPublishedVideo = true;
    // Dynamically create a container in the form of a DIV element for playing the local video track.
    const localPlayerContainer = document.createElement('div');
    // Specify the ID of the DIV container. You can use the uid of the local user.
    localPlayerContainer.id = this.options.uid;
    // localPlayerContainer.textContent = 'Local user ' + this.options.uid;
    localPlayerContainer.style.width = '200px';
    localPlayerContainer.style.height = '200px';
    const videoContentUser = document.getElementById('video-content-user');
    videoContentUser.append(localPlayerContainer);
    this.rtc.localPlayerContainer = localPlayerContainer;

    // Play the local video track.
    // Pass the DIV container and the SDK dynamically creates a player in the container for playing the local video track.
    this.rtc.localVideoTrack.play(localPlayerContainer);

    // When token-privilege-will-expire occurs, fetch a new token from the server and call renewToken to renew the token.
    this.rtc.client.on('token-privilege-will-expire', async function() {
      const response = await ApiHelper.getApi('ChannelApi')
        .agoraToken(self.currentChannel.id, {
          with: 'users,latest_message,users.latestReservation',
        });
      await self.rtc.client?.renewToken(response.data.token);
    });

    if (!this.hasCamera) {
      await this.rtc.localVideoTrack.setEnabled(false);
    }
    if (!this.hasMic) {
      await this.rtc.localAudioTrack.setEnabled(false);
    }
    console.log('publish success!');

    this.rtc.client.on('network-quality', async (stats) => {
      await this.handleNetworkQuantity(stats, localPlayerContainer);
    });

    this.isCall = true;
    this.isCanJoin = true;
  }

  async leaveCalling() {
    // Destroy the local audio and video tracks.
    this.rtc.localAudioTrack.close();
    this.rtc.localVideoTrack.close();

    // Traverse all remote users.
    this.rtc.client.remoteUsers.forEach((user) => {
      // Destroy the dynamically created DIV containers.
      const playerContainer = document.getElementById(user.uid);
      playerContainer && playerContainer.remove();
    });

    // Leave the channel.
    await this.rtc.client.leave();
    this.resetCallState();
    this.setVideoCall(false);
    this.$modal.hide('video-component');
  }

  beforeDestroy() {
    this.clearVideoRestoreWatchdog();
    this.rtc.client.leave();
    this.setVideoCall(false);
  }

  async handleNetworkQuantity(stats, localPlayerContainer) {
    const networkQuantity = stats.uplinkNetworkQuality;

    // Determine target quality level name
    let targetLevel = null;
    if (this.networkStatus.HIGH.quantity.includes(networkQuantity)) {
      targetLevel = this.networkStatus.HIGH.name;
    } else if (this.networkStatus.MEDIUM.quantity.includes(networkQuantity)) {
      targetLevel = this.networkStatus.MEDIUM.name;
    } else if (this.networkStatus.LOW.quantity.includes(networkQuantity)) {
      targetLevel = this.networkStatus.LOW.name;
    } else {
      // quality=0 (unknown) — skip, keep current display
      return;
    }

    // Record that a valid (non-zero) quality sample just arrived. The watchdog
    // uses this to distinguish "metric stuck at quality=0" (no recent sample →
    // safe to force-restore) from "network genuinely still weak" (LOW samples
    // keep arriving → do NOT force-restore to avoid a flicker loop).
    this._lastValidQualitySampleAt = Date.now();

    // Debounce: only apply after DEBOUNCE_THRESHOLD consecutive samples of the same level
    if (this._pendingQualityLevel === targetLevel) {
      this._pendingQualityCount += 1;
    } else {
      this._pendingQualityLevel = targetLevel;
      this._pendingQualityCount = 1;
    }

    // During the post-reconnect grace window, require a longer streak of
    // consecutive LOW samples before trusting it (see _reconnectGraceUntil).
    // HIGH/MEDIUM readings are never held back — they should recover the UI
    // as soon as possible.
    const isInReconnectGrace = this._reconnectGraceUntil
      && Date.now() < this._reconnectGraceUntil;
    const requiredCount = (targetLevel === this.networkStatus.LOW.name && isInReconnectGrace)
      ? this.RECONNECT_GRACE_LOW_THRESHOLD
      : this.DEBOUNCE_THRESHOLD;

    if (this._pendingQualityCount < requiredCount) {
      return;
    }

    // Skip if the level has already been applied — avoids repeated publish() calls
    // on every Agora network-quality tick (~2s) when quality is stable
    if (targetLevel === this._appliedLevel) {
      return;
    }
    this._appliedLevel = targetLevel;

    // Apply — level has been stable for enough samples and differs from last applied
    this.rtc.networkQuantity = networkQuantity;
    const localVideoContentUser = document.getElementById('video-content-user');

    if (targetLevel === this.networkStatus.HIGH.name
      || targetLevel === this.networkStatus.MEDIUM.name) {
      // Only re-enable and re-publish the video track if it was explicitly
      // disabled due to LOW quality. Calling publish() on an already-published
      // track on the very first quality event (when the track has never been
      // disabled) can throw and prevent rtc.networkStatus from being updated.
      if (this._videoDisabledByLowQuality) {
        this._videoDisabledByLowQuality = false;
        // Quality genuinely recovered on its own — the watchdog is no longer needed.
        this.clearVideoRestoreWatchdog();
        try {
          this.rtc.localVideoTrack.setEnabled(true);
          this.restartLocalVideoTrack(localPlayerContainer);
          // Only publish the video track; audio is already published since join.
          // Publishing an already-published track throws INVALID_OPERATION.
          if (!this.isPublishedVideo) {
            await this.rtc.client.publish([this.rtc.localVideoTrack]);
            this.isPublishedVideo = true;
          }
        } catch (error) {
          // Restore can fail if the track was closed or already published —
          // swallow it; the next quality sample will retry if needed.
        }

        if (localVideoContentUser) {
          localVideoContentUser.style.display = 'flex';
        }
      }
    }

    if (targetLevel === this.networkStatus.HIGH.name) {
      this.rtc.networkStatus = this.networkStatus.HIGH.name;
      this.rtc.localVideoTrack.setEncoderConfiguration({
        width: 640,
        height: 480,
        frameRate: 30,
        bitrate: 1000,
        bitrateMax: 1200,
      });
    } else if (targetLevel === this.networkStatus.MEDIUM.name) {
      this.rtc.networkStatus = this.networkStatus.MEDIUM.name;
      this.rtc.localVideoTrack.setEncoderConfiguration({
        width: 640,
        height: 480,
        frameRate: 10,
        bitrate: 300,
      });
    } else if (targetLevel === this.networkStatus.LOW.name) {
      this.rtc.networkStatus = this.networkStatus.LOW.name;
      // Guard: only execute side-effects once per LOW entry.
      // Without this, the TC4 fix (_appliedLevel reset on CONNECTED) would
      // allow this branch to re-run after every Agora reconnect while the
      // network is still weak, calling publish() on an already-disabled track
      // and triggering an INVALID_OPERATION error — the same class of bug as TC3.
      if (!this._videoDisabledByLowQuality) {
        this._videoDisabledByLowQuality = true;
        // Guard against the metric getting stuck (e.g. staying at quality=0
        // right after this) and leaving the local preview hidden forever even
        // though the connection is fine — see VIDEO_RESTORE_WATCHDOG_MS.
        this.scheduleVideoRestoreWatchdog();

        if (localVideoContentUser) {
          localVideoContentUser.style.display = 'none';
        }
        try {
          this.rtc.localVideoTrack.setEnabled(false);
          this.rtc.localVideoTrack.stop();
          // Audio is already published since join; only update the video track.
          // Publishing an already-published track throws INVALID_OPERATION.
          if (this.isPublishedVideo) {
            await this.rtc.client.publish([this.rtc.localVideoTrack]);
          }
        } catch (error) {
          // Swallow: track may be in a transitional state; the watchdog will
          // attempt restore if the video stays hidden too long.
        }
      }
    }
  }

  showPlanListModal() {
    this.$modal.show('plan-list-popup');
    setTimeout(() => {
      this.isShowPlanListModal = !this.isShowPlanListModal;
    }, 200);
  }

  destroyed() {
    this.leaveCalling();
  }
}
</script>

<style lang='scss'>
#video-content>div {
  width: 100%;
  height: 100% !important;
  border: 3px solid darkslategray;
  border-radius: 10px;
  overflow: hidden;
}

#video-content-user>div {
  width: 200px;
  height: 100%;
  margin-left: 10px;
  border: 3px solid darkslategray;
  border-radius: 10px;
  overflow: auto;
}

.video {
  &-component {
    height: 0 !important;
    position: relative !important;

    &.is-not-chat-tab {
      height: 0;

      .v--modal-box {
        width: 500px !important;
        height: 350px !important;
        max-width: 100vw !important;
        position: fixed !important;
      }

      .video-container {
        height: 350px;
      }

      .video-content-user > div {
        width: 130px !important;
        height: 130px !important;
      }

      .video-user {
        width: 130px;
        height: 130px;
      }

      .video-content {
        height: 57% !important;
      }

      @media (max-width: 767px) {
        .v--modal-box {
          width: 100% !important;
          height: 40vh !important;
          left: 0 !important;
          top: 0 !important;
        }

        .video-body {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
        }

        .video-container {
          height: auto;
          flex: 1;
          min-height: 0;
        }

        .video-content {
          height: 100% !important;
        }

        .video-content-user {
          position: absolute;
          right: 18px;
          bottom: 10px;
          height: auto;
          padding: 0;
          z-index: 100;
        }
      }
    }

    &.is-chat-tab {
      .v--modal-box {
        width: 540px !important;
        height: 350px !important;
        max-width: 100vw !important;
        position: fixed !important;
      }

      .video-container {
        height: 350px;
      }

      .video-content-user > div {
        width: 130px !important;
        height: 130px !important;
      }

      .video-user {
        width: 130px;
        height: 130px;
      }

      .video-content {
        height: 57% !important;
      }

      // Docked at the top of the viewport on mobile so the plan-list popup
      // (PlanListPopup.vue) can be stacked below it instead of overlapping.
      @media (max-width: 767px) {
        .v--modal-box {
          width: 100% !important;
          height: 40vh !important;
          left: 0 !important;
          top: 0 !important;
        }

        .video-body {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
        }

        .video-container {
          height: auto;
          flex: 1;
          min-height: 0;
        }

        .video-content {
          height: 100% !important;
        }

        .video-content-user {
          position: absolute;
          right: 18px;
          bottom: 10px;
          height: auto;
          padding: 0;
          z-index: 100;
        }
      }
    }

    &.is-fullscreen {
      .v--modal-box {
        width: 100vw !important;
        height: 100vh !important;
        top: 0 !important;
        left: 0 !important;
        margin: 0 !important;
      }

      .video-container {
        height: calc(100vh);
      }

      .video-header {
        font-size: 14px;
      }

      .video-content-user > div {
        width: 160px !important;
        height: 160px !important;
      }

      .video-user {
        width: 160px;
        height: 160px;
      }

      .video-content {
        height: calc(100vh - 220px) !important;
      }

      .fullscreen-btn {
        position: absolute;
        top: 10px;
        right: 10px;
        background: transparent;
        border: none;
        padding: 6px;
        cursor: pointer;

        svg {
          width: 20px;
          height: 20px;
          color: #fff;
        }
      }
    }

    .video-controls {
      position: absolute;
      top: 45px;
      right: 5px;
      z-index: 9;
      display: flex;
      align-items: center;
      gap: 7px;

      button {
        border: 2px solid #fff;
        border-radius: 4px;
        background: transparent;
        padding: 2px 4px;

        &:focus {
          outline: none;
        }
      }
    }

    @media (max-width: 767px) {
      .video-controls {
        top: 8px;
        z-index: 20;
      }
    }
  }

  &-header {
    background-color: #000;
    padding: 10px;
    color: #fff;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    font-size: 12px;

    span {
      margin-left: 10px;
    }

    @media (max-width: 767px) {
      flex-direction: column;
      align-items: flex-start;
      flex-wrap: wrap;
      flex-shrink: 0;
      gap: 4px;
      padding-right: 40px;

      > div {
        width: 100%;
        min-width: 0;
      }

      // Reset the network-status span's height:0/position:relative (inherited
      // from the unrelated .video-component modal class) so it doesn't
      // collapse and overlap the title/notification on narrow screens.
      .video-component {
        height: auto !important;
        position: static !important;
        display: inline-block;
        white-space: normal;
        word-break: break-word;
      }
    }
  }

  &-content {
    z-index: 1;
    height: 80%;
    padding: 10px;
    padding-bottom: 0;

    @media (max-width: 767px) {
      box-sizing: border-box;
    }
  }

  &-item {
    padding: 10px 10px 0;
    z-index: 1000;
  }

  &-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: center;
    position: absolute;
    bottom: -12px;
    width: 100%;
    padding: 10px 10px 10px;
  }

  &-btn {
    color: #fff;
    font-weight: bold;
    border-radius: 6px;
    padding: 10px;
    border: unset;

    &-call {
      background: #008505;
    }

    &-end {
      background: #fc1d1d;
    }

    &-plan {
      background: #008505;
    }
  }

  &-container {
    position: relative;
    height: 90vh;
    background-color: #263238;
  }

  &-notification {
    background: black;
    color: rgb(94, 255, 51);
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    animation: slide-right-left 0.5s ease-out;
  }

  @keyframes slide-right-left {
    from {
      transform: translateX(100%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  &-user {
    position: absolute;
    right: 18px;
    bottom: 10px;
    width: 200px;
    height: 200px;
    z-index: 100;
    background-color: transparent;
  }
}

.video-content-user {
  height: 20%;
  padding: 10px;
  display: flex;
  flex-direction: row-reverse;
}

.mic-icon-user {
  position: absolute;
  top: 58px;
  z-index: 1000;
  height: 30px;
  left: 20px;
}

.network-high {
  background: rgba(131, 128, 128, 0.6);
  color: #5eff33;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.network-medium {
  background: rgba(131, 128, 128, 0.6);
  color: #ffca33;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.network-low {
  background: rgba(131, 128, 128, 0.6);
  color: #ff3333;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}
</style>
